import type { Draw, DrawMap } from "leaflet"
import L from "leaflet"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { useMap } from "react-leaflet"
import { connect } from "react-redux"
import type { Dispatch } from "redux"

import { useTracker } from "../../../hooks/useTracker"
import { AnalyticsElementModule } from "../../../models"
import { MapViewerPanel, mapViewerSwitchEditPanel, mapViewerTogglePanel } from "../../../redux"
import type { AppState } from "../../../redux"
import { TooltipIconButton } from "../../atoms"

export interface IDrawShapeControlOwnProps {
  controlPanel: MapViewerPanel
  tooltip: string
  icon: ReactElement
  createFeature: (map: DrawMap) => Draw.Feature
}

export interface IDrawShapeControlStateProps {
  isDrawing: boolean
  toggleDrawing: (panel: MapViewerPanel, open: boolean) => void
  selectControl: (panel: MapViewerPanel) => void
}

function DrawShapeControlView(props: IDrawShapeControlOwnProps & IDrawShapeControlStateProps): ReactElement {
  const { isDrawing, controlPanel, tooltip, icon, createFeature, toggleDrawing, selectControl } = props
  const tracker = useTracker()
  const [currentShape, setCurrentShape] = useState<Draw.Feature>()

  const map: DrawMap = useMap() as DrawMap
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!isDrawing && currentShape) {
      turnOffDrawing()
    }

    map.removeEventListener(L.Draw.Event.CREATED, onDrawShapeEnd)
    map.addEventListener(L.Draw.Event.CREATED, onDrawShapeEnd)
  }, [isDrawing])

  function onDrawShapeEnd(): void {
    if (isDrawing) {
      toggleDrawing(controlPanel, false)
      setCurrentShape(undefined)
    }
  }

  function trackDrawStart(): void {
    tracker.elementClicked({
      module: AnalyticsElementModule.MapDrawingPane,
      itemId: getDrawItemId(),
      type: "button",
      elementId: "draw-control"
    })
  }

  function getDrawItemId(): string {
    switch (controlPanel) {
      case MapViewerPanel.DrawSquare:
        return "square"
      case MapViewerPanel.DrawArrow:
        return "arrow"
      case MapViewerPanel.DrawCircle:
        return "circle"
      case MapViewerPanel.DrawText:
        return "text"
      default:
        return "freedraw"
    }
  }

  function startDrawing(): void {
    if (!isDrawing && !currentShape) {
      trackDrawStart()
      const shapeEdit = createFeature(map)
      shapeEdit.enable()
      setCurrentShape(shapeEdit)
      selectControl(controlPanel)
    } else if (currentShape) {
      turnOffDrawing()
      toggleDrawing(controlPanel, false)
    }
  }

  function turnOffDrawing(): void {
    if (!currentShape) return
    currentShape.disable()
    setCurrentShape(undefined)
  }

  return (
    <TooltipIconButton onClick={startDrawing} selected={isDrawing} placement="right" tooltip={tooltip} icon={icon} />
  )
}

function mapStateToProps(state: AppState, ownProps: IDrawShapeControlOwnProps) {
  return {
    ...ownProps,
    isDrawing: state.mapViewer.panels.get(ownProps.controlPanel) || false
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    toggleDrawing: (panel: MapViewerPanel, open: boolean) => dispatch(mapViewerTogglePanel(panel, open)),
    selectControl: (panel: MapViewerPanel) => dispatch(mapViewerSwitchEditPanel(panel))
  }
}

export const DrawShapeControl = connect(mapStateToProps, mapDispatchToProps)(DrawShapeControlView)
